import "./index.shared";
import {createRoot} from "react-dom/client";
import {QueryClient, QueryClientProvider} from "react-query";
import React, {StrictMode, useEffect, useState} from "react";
import {ReactQueryDevtools} from "react-query/devtools";
import {LocaleProvider} from "./localization/LocaleProvider";
import {useShopifyFetch} from "./shopify/useShopifyFetch";
import {useLocale} from "./localization/useLocale";
import {Product} from "@shopify/hydrogen-react/storefront-api-types";
import Preloader from "./Preloader";
import {CountryCode, LanguageCode} from "@shopify/hydrogen-react/dist/types/storefront-api-types";
import LocalizedShopifyProvider from "./LocalizedShopifyProvider";

type MinimalRecipeCardProps = {
  recipe: Partial<Product>;
}

const MinimalRecipeCard = ({recipe}: MinimalRecipeCardProps): React.ReactElement =>
  <div className="col-12 col-md-6 col-lg-4 mt-4 mb-4">
    <a href={recipe.onlineStoreUrl}>
      <div className="respimgsize tt-product product-parent options-js thumbprod-center recipe-card swiper-slide">
        <img src={recipe.featuredImage?.url ?? small_image} alt={recipe.title} width="300" height="200"
             loading="lazy" />
        <div className="tt-description pb-0">
          <h6 className="recipe-card-title mb-0">{recipe.title}</h6>
        </div>
      </div>
    </a>
  </div>;

const getRandomRecipeIds = async (): Promise<string[]> => {
  const todayAsIsoString = new Date().toISOString().split("T")[0];
  const slotsUrl = `https://api.cleankitchen.ee/v2/recipes/summary?country=Estonia&date=${todayAsIsoString}&limit=10`;

  try {
    const slotsResponse = await fetch(slotsUrl, {
      method: "GET",
      mode: "cors",
      cache: "no-cache",
      headers: {
        "content-type": "application/json",
      },
      credentials: "same-origin",
    });
    const slotsJson = await slotsResponse.json();
    if (slotsJson.length >= 6) {
      return slotsJson
        .sort(() => 0.5 - Math.random())
        .slice(0, 6);
    }
  } catch (err) {
    console.log(err);
    return [];
  }
};

const getMinimalRecipesQuery = (countryCode: CountryCode, languageCode: LanguageCode) => `#graphql
query GetMinimalRecipes($ids: [ID!]!) @inContext(country: ${countryCode}, language: ${languageCode}) {
    nodes(ids: $ids) {
        id
        ... on Product {
            title
            onlineStoreUrl
            featuredImage {
                url(transform: {maxHeight:200, maxWidth: 300, crop: CENTER, scale: 2})
            }
        }
    }
}
`;

const WeeklyRecipes = (): React.ReactElement => {
  const storefrontFetch = useShopifyFetch();
  const {locale} = useLocale();

  const [recipeIds, setRecipesIds] = useState<string[]>([]);
  const [recipes, setRecipes] = useState<Partial<Product>[]>([]);

  useEffect(() => {
    getRandomRecipeIds().then(randomRecipeIds => setRecipesIds(randomRecipeIds));
  }, []);

  useEffect(() => {
    storefrontFetch({
      query: getMinimalRecipesQuery(locale.countryCode, locale.languageCode),
      variables: {ids: recipeIds},
    }).then(result => {
      const fetchedRecipes = result.data.nodes as Partial<Product>[];
      setRecipes(fetchedRecipes);
    });
  }, [recipeIds]);

  if (recipes.length === 0) {
    return <Preloader />;
  }

  return <>
    {
      recipes.map((recipe) => <MinimalRecipeCard key={recipe.id} recipe={recipe} />)
    }
  </>;
};

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: Infinity,
    },
  },
});

const currentWeekRecipesContainer = document.getElementById(
  "current-week-recipes",
);

if (currentWeekRecipesContainer) {
  const root = createRoot(currentWeekRecipesContainer);
  root.render(
    <QueryClientProvider client={queryClient}>
      <StrictMode>
        <ReactQueryDevtools />
        <LocaleProvider>
          <LocalizedShopifyProvider>
            <WeeklyRecipes />
          </LocalizedShopifyProvider>
        </LocaleProvider>
      </StrictMode>
    </QueryClientProvider>,
  );
}
